import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ShoppingBag, Store, Shield, Phone } from 'lucide-react';
import { GoogleIcon } from '@/components/ui/google-icon';

export default function Auth() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [role, setRole] = useState<'buyer' | 'seller' | 'admin'>('buyer');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    fullName: '',
    phone: '',
    businessName: '',
    businessDescription: '',
    businessCategory: '',
  });
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for return URL, message, and affiliate ref code
  const returnUrl = searchParams.get('returnUrl');
  const message = searchParams.get('message');
  const affiliateRef = searchParams.get('ref');
  const isFromCheckout = returnUrl?.includes('/share/') || returnUrl === '/payment-success';

  // Store affiliate ref in sessionStorage for tracking after signup
  useEffect(() => {
    if (affiliateRef) {
      // Store in both localStorage (persists across tabs) and sessionStorage
      localStorage.setItem('affiliate_ref', affiliateRef);
      sessionStorage.setItem('affiliate_ref', affiliateRef);
      // Default to signup mode when coming from affiliate link
      setMode('signup');
    }
  }, [affiliateRef]);

  useEffect(() => {
    // Show message if present
    if (message) {
      toast({
        title: 'Authentication Required',
        description: message,
      });
    }

    // If coming from checkout, force buyer role
    if (isFromCheckout) {
      setRole('buyer');
    }
  }, [message, isFromCheckout, toast]);

  useEffect(() => {
    // Check if user is already logged in
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Get user profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('id', session.user.id)
            .single();
          
          // If coming from checkout, redirect back there
          if (returnUrl) {
            navigate(returnUrl);
            return;
          }
          
          // Otherwise redirect based on role
          const { data: userRole } = await supabase.rpc('get_user_role', { _user_id: session.user.id });
          
          if (!userRole) {
            navigate('/role-selection');
          } else if (userRole === 'seller') {
            navigate('/seller/dashboard');
          } else if (userRole === 'admin') {
            navigate('/admin');
          } else {
            navigate(profile?.onboarding_completed ? '/buyer/orders' : '/products');
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Add a small delay to ensure profile is created
        setTimeout(async () => {
          // Track affiliate referral - check storage AND user metadata
          const storedAffiliateRef = 
            localStorage.getItem('affiliate_ref') || 
            sessionStorage.getItem('affiliate_ref') ||
            session.user.user_metadata?.affiliate_ref;
            
          if (storedAffiliateRef) {
            try {
              const { data: affiliateLink } = await supabase
                .from('affiliate_links')
                .select('id, active')
                .eq('code', storedAffiliateRef)
                .single();
              
              if (affiliateLink && affiliateLink.active) {
                const { data: existingReferral } = await supabase
                  .from('affiliate_referrals')
                  .select('id')
                  .eq('referred_user_id', session.user.id)
                  .maybeSingle();
                
                if (!existingReferral) {
                  await supabase
                    .from('affiliate_referrals')
                    .insert({
                      affiliate_link_id: affiliateLink.id,
                      referred_user_id: session.user.id,
                    });
                  console.log('Affiliate referral tracked on auth change');
                }
              }
            } catch (err) {
              console.error('Error tracking affiliate:', err);
            } finally {
              localStorage.removeItem('affiliate_ref');
              sessionStorage.removeItem('affiliate_ref');
            }
          }

          const { data: profile } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('id', session.user.id)
            .single();
          
          // If coming from checkout, redirect back
          if (returnUrl) {
            navigate(returnUrl);
            return;
          }
          
          // For OAuth users, get role securely
          const { data: userRole } = await supabase.rpc('get_user_role', { _user_id: session.user.id });
          
          if (!userRole) {
            navigate('/role-selection');
          } else if (userRole === 'seller') {
            navigate('/seller/dashboard');
          } else if (userRole === 'admin') {
            navigate('/admin');
          } else {
            navigate(profile?.onboarding_completed ? '/buyer/orders' : '/products');
          }
        }, 500);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, returnUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'signup' && formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (mode === 'signup' && formData.password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long',
        variant: 'destructive',
      });
      return;
    }

    // Validate all required seller fields
    if (mode === 'signup' && role === 'seller' && !isFromCheckout) {
      if (!formData.businessName.trim()) {
        toast({
          title: 'Error',
          description: 'Business Name is required for sellers',
          variant: 'destructive',
        });
        return;
      }
      if (!formData.businessCategory.trim()) {
        toast({
          title: 'Error',
          description: 'Business Category is required for sellers',
          variant: 'destructive',
        });
        return;
      }
      if (!formData.businessDescription.trim()) {
        toast({
          title: 'Error',
          description: 'Business Description is required for sellers',
          variant: 'destructive',
        });
        return;
      }
    }

    setLoading(true);

    try {
      if (mode === 'signup') {
        // For users from checkout, always set role as buyer
        const finalRole = isFromCheckout ? 'buyer' : role;
        
        // Get affiliate ref from storage to include in user metadata
        const storedAffiliateRef = localStorage.getItem('affiliate_ref') || sessionStorage.getItem('affiliate_ref');
        
        const { data, error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: returnUrl ? `${window.location.origin}${returnUrl}` : `${window.location.origin}/auth/callback`,
            data: {
              role: finalRole,
              username: formData.username,
              full_name: formData.fullName,
              phone: formData.phone,
              business_name: finalRole === 'seller' ? formData.businessName : null,
              business_description: finalRole === 'seller' ? formData.businessDescription : null,
              business_category: finalRole === 'seller' ? formData.businessCategory : null,
              affiliate_ref: storedAffiliateRef || null, // Store in user metadata
            },
          },
        });

        if (error) {
          if (error.message.includes('User already registered')) {
            throw new Error('An account with this email already exists. Please login instead.');
          }
          throw error;
        }

        if (data.user) {
          toast({
            title: 'Success!',
            description: 'Please check your email to confirm your account. If you don\'t see it, check your spam folder.',
          });
          setMode('login'); // Switch to login tab
        }
      } else {
        // Login - authenticate first, then do secondary checks in parallel
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (error) {
          // Track failed login attempt (non-blocking)
          supabase.rpc('track_failed_login', { p_email: formData.email }).then(() => {});
          
          if (error.message.includes('Invalid login credentials')) {
            throw new Error('Invalid email or password. Please try again.');
          }
          throw error;
        }

        if (data.user) {
          // Fetch profile and role in parallel for speed
          const [profileResult, roleResult] = await Promise.all([
            supabase.from('profiles').select('onboarding_completed, login_locked_until').eq('id', data.user.id).maybeSingle(),
            supabase.rpc('get_user_role', { _user_id: data.user.id }),
          ]);

          // Reset login attempts (non-blocking)
          supabase.rpc('reset_login_attempts', { p_user_id: data.user.id }).then(() => {});

          const profile = profileResult.data;
          const userRole = roleResult.data;

          // Check if account is locked
          if (profile?.login_locked_until && new Date(profile.login_locked_until) > new Date()) {
            await supabase.auth.signOut();
            const minutesLeft = Math.ceil((new Date(profile.login_locked_until).getTime() - Date.now()) / 60000);
            toast({
              title: "Account Locked",
              description: `Too many failed attempts. Please try again in ${minutesLeft} minutes.`,
              variant: "destructive",
            });
            setLoading(false);
            return;
          }

          toast({
            title: 'Welcome back!',
            description: 'You have successfully logged in.',
          });

          // If coming from checkout, redirect back
          if (returnUrl) {
            navigate(returnUrl);
            return;
          }

          if (!userRole) {
            navigate('/role-selection');
          } else if (userRole === 'seller') {
            navigate('/seller/dashboard');
          } else if (userRole === 'admin') {
            navigate('/admin');
          } else {
            navigate(profile?.onboarding_completed ? '/buyer/orders' : '/products');
          }
        }
      }
    } catch (error: any) {
      toast({
        title: 'Authentication Failed',
        description: error.message || 'An error occurred during authentication',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google') => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) throw error;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred during social login',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handlePhoneLogin = async () => {
    if (!formData.phone) {
      toast({
        title: 'Error',
        description: 'Please enter your phone number',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone: formData.phone,
      });

      if (error) throw error;

      toast({
        title: 'OTP Sent',
        description: 'Please check your phone for the verification code.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred during phone login',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const roleIcons = {
    buyer: <ShoppingBag className="h-5 w-5" />,
    seller: <Store className="h-5 w-5" />,
    admin: <Shield className="h-5 w-5" />,
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 py-12">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">
            {mode === 'login' ? 'Welcome Back' : 'Create an Account'}
          </CardTitle>
          <CardDescription className="text-center">
            {mode === 'login' 
              ? 'Log in to your SabiTradeNG account' 
              : 'Join SabiTradeNG to start trading securely'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" value={mode} onValueChange={(v) => setMode(v as 'login' | 'signup')}>
            <TabsList className="grid w-full grid-cols-2 h-12">
              <TabsTrigger value="login" className="text-sm sm:text-base">Login</TabsTrigger>
              <TabsTrigger value="signup" className="text-sm sm:text-base">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              {loginMethod === 'email' ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      'Log In'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setLoginMethod('phone')}
                    disabled={loading}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Use Phone Instead
                  </Button>
                </form>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); handlePhoneLogin(); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-phone">Phone Number</Label>
                    <Input
                      id="login-phone"
                      type="tel"
                      placeholder="+234 800 000 0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setLoginMethod('email')}
                    disabled={loading}
                  >
                    Use Email Instead
                  </Button>
                </form>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Button
                  variant="google"
                  onClick={() => handleSocialLogin('google')}
                  disabled={loading}
                  className="w-full"
                >
                  <GoogleIcon className="mr-2 h-4 w-4" />
                  Continue with Google
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              {/* Role Selection - Hide for checkout users */}
              {!isFromCheckout && (
                <div className="space-y-2">
                  <Label>Select Your Role</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {(['buyer', 'seller', 'admin'] as const).map((r) => (
                      <Button
                        key={r}
                        type="button"
                        variant={role === r ? 'default' : 'outline'}
                        onClick={() => setRole(r)}
                        className="flex flex-col items-center gap-1 h-auto py-3"
                        disabled={loading}
                      >
                        {roleIcons[r]}
                        <span className="capitalize">{r}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Show message for checkout users */}
              {isFromCheckout && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                  <p className="text-sm text-center">
                    Creating a buyer account to complete your purchase
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      placeholder="johndoe"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email *</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+234 800 000 0000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>

                {role === 'seller' && !isFromCheckout && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="businessName">Business Name *</Label>
                      <Input
                        id="businessName"
                        placeholder="Your Business Name"
                        value={formData.businessName}
                        onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessCategory">Business Category *</Label>
                      <Input
                        id="businessCategory"
                        placeholder="e.g., Electronics, Fashion, Food"
                        value={formData.businessCategory}
                        onChange={(e) => setFormData({ ...formData, businessCategory: e.target.value })}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessDescription">Business Description *</Label>
                      <Input
                        id="businessDescription"
                        placeholder="Describe what your business offers"
                        value={formData.businessDescription}
                        onChange={(e) => setFormData({ ...formData, businessDescription: e.target.value })}
                        required
                        disabled={loading}
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Min 6 characters"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Re-enter password"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Button
                  variant="google"
                  onClick={() => handleSocialLogin('google')}
                  disabled={loading}
                  className="w-full"
                >
                  <GoogleIcon className="mr-2 h-4 w-4" />
                  Continue with Google
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="underline underline-offset-4 hover:text-primary">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline underline-offset-4 hover:text-primary">
              Privacy Policy
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}